import { useState, useEffect, useContext, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import ConversationSidebar from "@/components/ConversationSidebar";
import { Send, Sparkles, Loader2, Menu, PanelLeftClose, AlertTriangle, Mic, MicOff, Square } from "lucide-react";
import { trackChatStarted } from "@/lib/analytics";

const ChatAI = () => {
  const { user, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Conversations state
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  
  // Input state
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingConversations, setLoadingConversations] = useState(true);
  
  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showSafetyAlert, setShowSafetyAlert] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  // Context from URL (when coming from Journal, Exercise, etc.)
  const contextType = searchParams.get("context");
  const contextId = searchParams.get("id");
  const contextTitle = searchParams.get("title");
  
  const messagesEndRef = useRef(null);

  // Load conversations on mount
  useEffect(() => {
    fetchConversations();
  }, []);

  // Handle context from URL params
  useEffect(() => {
    if (contextType && contextId && !loadingConversations) {
      // Check if we already have a conversation for this context
      const existingConv = conversations.find(
        c => c.context_type === contextType && c.context_id === contextId
      );
      
      if (existingConv) {
        selectConversation(existingConv.id);
      } else {
        // Start fresh for this context - will create conversation on first message
        setActiveConversationId(null);
        setMessages([]);
      }
    }
  }, [contextType, contextId, loadingConversations, conversations]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data.conversations || []);
      
      // If no context params and we have conversations, select the most recent
      if (!contextType && response.data.conversations?.length > 0) {
        const mostRecent = response.data.conversations[0];
        selectConversation(mostRecent.id);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConversations(false);
    }
  };

  const selectConversation = async (conversationId) => {
    setActiveConversationId(conversationId);
    setMessages([]);
    setSidebarOpen(false);
    
    // Clear URL params when selecting existing conversation
    if (searchParams.has("context")) {
      setSearchParams({});
    }
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error loading conversation:", error);
      toast.error(t("Error al cargar la conversación", "Error en carregar la conversa"));
    }
  };

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setMessages([]);
    setSidebarOpen(false);
    
    // Clear URL params
    setSearchParams({});
  };

  const handleDeleteConversation = async (conversationId) => {
    const confirmMsg = t(
      "¿Eliminar esta conversación?",
      "Eliminar aquesta conversa?"
    );
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConversations(prev => prev.filter(c => c.id !== conversationId));
      
      if (activeConversationId === conversationId) {
        handleNewConversation();
      }
      
      toast.success(t("Conversación eliminada", "Conversa eliminada"));
    } catch (error) {
      console.error("Error deleting conversation:", error);
      toast.error(t("Error al eliminar", "Error en eliminar"));
    }
  };

  const handleUpdateTitle = async (conversationId, newTitle) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API}/conversations/${conversationId}/title`,
        { title: newTitle },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setConversations(prev => 
        prev.map(c => c.id === conversationId ? { ...c, title: newTitle } : c)
      );
    } catch (error) {
      console.error("Error updating title:", error);
      toast.error(t("Error al actualizar", "Error en actualitzar"));
    }
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      toast.info(t("Grabando... Pulsa de nuevo para parar", "Gravant... Prem de nou per aturar"));
    } catch (error) {
      console.error("Error starting recording:", error);
      toast.error(t(
        "No se pudo acceder al micrófono. Verifica los permisos.",
        "No s'ha pogut accedir al micròfon. Verifica els permisos."
      ));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    setIsTranscribing(true);
    
    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');
      formData.append('language', language === 'ca' ? 'ca' : 'es');

      const response = await axios.post(`${API}/voice/transcribe`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.text) {
        setInput(prev => prev + (prev ? ' ' : '') + response.data.text);
        toast.success(t("Transcripción completada", "Transcripció completada"));
      }
    } catch (error) {
      console.error("Transcription error:", error);
      toast.error(t(
        "Error al transcribir el audio",
        "Error en transcriure l'àudio"
      ));
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleVoiceButton = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Add user message optimistically
    const userMsg = {
      role: "user",
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMsg]);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        message: userMessage,
        language: language,
        conversation_id: activeConversationId
      };

      // Add context if present (for new conversations from Journal, etc.)
      if (!activeConversationId && contextType && contextId) {
        payload.context_type = contextType;
        payload.context_id = contextId;
      }

      const response = await axios.post(`${API}/chat`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const aiMsg = {
        role: "assistant",
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMsg]);
      trackChatStarted();
      if (response.data.safety_alert) {
        setShowSafetyAlert(true);
      }

      // If this was a new conversation, update state
      if (!activeConversationId && response.data.conversation_id) {
        setActiveConversationId(response.data.conversation_id);
        // Refresh conversations list
        fetchConversations();
        // Clear URL params now that conversation is created
        if (searchParams.has("context")) {
          setSearchParams({});
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(t("Error al enviar mensaje", "Error en enviar missatge"));
      // Remove optimistic user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const getContextBanner = () => {
    if (!contextType || activeConversationId) return null;
    
    let text = "";
    switch (contextType) {
      case "daily_feeling":
        text = t("Nueva conversación sobre tu sentir de hoy", "Nova conversa sobre el teu sentir d'avui");
        break;
      case "journal":
        text = t("Nueva conversación sobre tu entrada de diario", "Nova conversa sobre la teva entrada de diari");
        break;
      case "exercise":
        text = t("Nueva conversación sobre el ejercicio", "Nova conversa sobre l'exercici");
        break;
      case "lesson":
        text = t("Nueva conversación sobre la lección", "Nova conversa sobre la lliçó");
        break;
      default:
        return null;
    }
    
    return (
      <div className="bg-teal-500/10 border border-teal-500/20 rounded-lg px-4 py-2 mb-4 text-sm text-teal-300">
        {text}
      </div>
    );
  };

  if (loadingConversations) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">
            {t("Cargando...", "Carregant...")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="flex h-[calc(100vh-64px)]">
        {/* Sidebar */}
        <ConversationSidebar
          conversations={conversations}
          activeConversationId={activeConversationId}
          onSelectConversation={selectConversation}
          onNewConversation={handleNewConversation}
          onDeleteConversation={handleDeleteConversation}
          onUpdateTitle={handleUpdateTitle}
          language={language}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Mobile header with menu button - STICKY */}
          <div className="md:hidden flex items-center gap-3 p-4 border-b border-slate-800 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-10">
            <button
              data-testid="mobile-sidebar-toggle"
              onClick={() => setSidebarOpen(true)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-white truncate">
              {activeConversationId 
                ? conversations.find(c => c.id === activeConversationId)?.title || t("Chat", "Xat")
                : t("Nueva conversación", "Nova conversa")
              }
            </h1>
          </div>

          {/* Desktop header - STICKY */}
          <div className="hidden md:flex items-center justify-between p-4 border-b border-slate-800 bg-slate-950/95 backdrop-blur-sm sticky top-0 z-10">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-slate-800"
                title={t("Toggle sidebar", "Alternar barra lateral")}
              >
                <PanelLeftClose className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-white">
                {activeConversationId 
                  ? conversations.find(c => c.id === activeConversationId)?.title || t("Chat", "Xat")
                  : t("Nueva conversación", "Nova conversa")
                }
              </h1>
            </div>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {getContextBanner()}
            
            {/* Safety Alert Modal */}
            {showSafetyAlert && (
              <div data-testid="safety-alert" className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
                <div className="bg-slate-900 border border-amber-500/30 rounded-2xl p-6 max-w-md w-full shadow-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <AlertTriangle className="w-6 h-6 text-amber-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-amber-300">
                      {t("Nota importante", "Nota important")}
                    </h3>
                  </div>
                  <p className="text-gray-300 leading-relaxed mb-6">
                    {t(
                      "Esta aplicación es una herramienta de ayuda extra para la autorregulación emocional personal. Si estás pasando por un momento difícil, te recomendamos consultar con un profesional de la salud mental.",
                      "Aquesta aplicació és una eina d'ajuda extra per l'autoregulació emocional personal. Si estàs passant per un moment difícil, et recomanem consultar amb un professional de la salut mental."
                    )}
                  </p>
                  <button
                    data-testid="safety-alert-close"
                    onClick={() => setShowSafetyAlert(false)}
                    className="w-full py-3 rounded-xl bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-medium transition-colors"
                  >
                    {t("Entendido", "Entès")}
                  </button>
                </div>
              </div>
            )}
            
            {messages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center px-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center mb-4">
                  <Sparkles className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-300 mb-2">
                  {t("¡Hola, " + user.name + "!", "Hola, " + user.name + "!")}
                </h3>
                <p className="text-gray-400 max-w-md">
                  {t(
                    "Estoy aquí para guiarte en tu camino de automaestría. ¿En qué puedo ayudarte hoy?",
                    "Estic aquí per guiar-te en el teu camí d'autoconeixement. En què et puc ajudar avui?"
                  )}
                </p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div
                  key={index}
                  data-testid={`message-${index}`}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] md:max-w-[70%] rounded-2xl p-4 ${
                      msg.role === "user"
                        ? "bg-teal-400/20 border border-teal-400/30 text-gray-100"
                        : "bg-slate-800/80 border border-slate-700 text-gray-200"
                    }`}
                  >
                    {msg.role === "assistant" && (
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4 text-teal-300" />
                        <span className="text-xs font-semibold text-teal-300">
                          {t("Guía", "Guia")}
                        </span>
                      </div>
                    )}
                    <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                  </div>
                </div>
              ))
            )}
            
            {loading && (
              <div data-testid="loading-indicator" className="flex justify-start">
                <div className="bg-slate-800/80 border border-slate-700 rounded-2xl p-4 flex items-center gap-2">
                  <Loader2 className="w-5 h-5 text-teal-300 animate-spin" />
                  <span className="text-gray-400">{t("Pensando...", "Pensant...")}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="p-4 border-t border-slate-800">
            <form data-testid="chat-input-form" onSubmit={handleSend} className="max-w-4xl mx-auto">
              <div className="flex gap-2 items-end bg-slate-900/50 rounded-2xl p-3 border border-slate-700">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("Escribe tu mensaje...", "Escriu el teu missatge...")}
                  disabled={loading || isTranscribing}
                  rows={1}
                  className="flex-1 bg-transparent border-none text-white placeholder-gray-500 focus:outline-none resize-none overflow-y-auto max-h-32"
                  style={{ minHeight: '24px' }}
                  onInput={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 128) + 'px';
                  }}
                />
                
                {/* Voice Recording Button */}
                <button
                  data-testid="voice-btn"
                  type="button"
                  onClick={handleVoiceButton}
                  disabled={loading || isTranscribing}
                  className={`flex items-center justify-center w-10 h-10 rounded-xl transition-colors ${
                    isRecording 
                      ? 'bg-red-500 hover:bg-red-400 animate-pulse' 
                      : isTranscribing
                        ? 'bg-slate-700 opacity-50'
                        : 'bg-slate-700 hover:bg-slate-600'
                  } text-white`}
                  title={isRecording ? t("Parar grabación", "Aturar gravació") : t("Grabar voz", "Gravar veu")}
                >
                  {isTranscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isRecording ? (
                    <Square className="w-4 h-4" />
                  ) : (
                    <Mic className="w-5 h-5" />
                  )}
                </button>
                
                {/* Send Button */}
                <button
                  data-testid="send-btn"
                  type="submit"
                  disabled={loading || !input.trim() || isTranscribing}
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-teal-500 hover:bg-teal-400 disabled:bg-slate-700 disabled:opacity-50 text-white transition-colors"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2 text-center">
                {t(
                  "Esta app es una herramienta de ayuda extra. Consulta un profesional si lo necesitas.",
                  "Aquesta app és una eina d'ajuda extra. Consulta un professional si ho necessites."
                )}
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatAI;
